import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { campaigns as campaignsApi, type ActiveCampaign } from '../api/client';

interface CampaignContextType {
  campaign: ActiveCampaign | null;
}

const CampaignContext = createContext<CampaignContextType>({ campaign: null });

interface CampaignProviderProps {
  children: ReactNode;
  /** SSR-seeded active campaign (window.__INITIAL_DATA__.activeCampaign).
   *  Present only on the homepage; when absent we fetch once on mount so
   *  client-side navigation into the homepage still gets it. */
  initialCampaign?: ActiveCampaign | null;
  /** Did the SSR payload include the campaign key at all? Distinguishes
   *  "seeded as null" (no active campaign) from "not seeded" (must fetch). */
  seeded?: boolean;
}

export function CampaignProvider({ children, initialCampaign = null, seeded = false }: CampaignProviderProps) {
  const [campaign, setCampaign] = useState<ActiveCampaign | null>(initialCampaign);

  useEffect(() => {
    if (seeded) return; // homepage SSR already provided it
    let cancelled = false;
    campaignsApi.active()
      .then((c) => { if (!cancelled) setCampaign(c); })
      .catch(() => { /* non-critical decoration — ignore */ });
    return () => { cancelled = true; };
  }, [seeded]);

  return (
    <CampaignContext.Provider value={{ campaign }}>
      {children}
    </CampaignContext.Provider>
  );
}

export function useActiveCampaign(): ActiveCampaign | null {
  return useContext(CampaignContext).campaign;
}
